/**
 * EditionGallery
 *
 * Buyer-facing artwork picker for edition (print-style) drops.
 * Shows each edition with its image, price, and remaining copies;
 * the buyer selects the artwork they want to mint.
 *
 * See docs/specs/EDITION_LAUNCHES.md
 */

import React from 'react';
import { FeatherCheck } from '@subframe/core';
import { getIpfsImageUrl, type DropEdition } from '../../api/drops';

export interface EditionGalleryProps {
  editions: DropEdition[];
  selectedEditionId: string | null;
  onSelectEdition: (editionId: string | null) => void;
  /** Disable interaction (e.g. while a mint is in flight) */
  disabled?: boolean;
}

function editionImageSrc(edition: DropEdition): string | null {
  return (
    edition.thumbnail_url ||
    edition.image_url ||
    getIpfsImageUrl(edition.image_cid)
  );
}

export const EditionGallery: React.FC<EditionGalleryProps> = ({
  editions,
  selectedEditionId,
  onSelectEdition,
  disabled = false,
}) => {
  if (editions.length === 0) {
    return null;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <span className="text-body-bold font-body-bold text-default-font">
          Pick an artwork
        </span>
        <span className="text-caption font-caption text-subtext-color">
          {editions.filter((e) => !e.sold_out).length} of {editions.length} available
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {editions.map((edition) => {
          const selected = edition.id === selectedEditionId;
          const imageSrc = editionImageSrc(edition);
          const priceXrp = edition.effective_price_drops / 1_000_000;

          return (
            <button
              key={edition.id}
              type="button"
              disabled={disabled || edition.sold_out}
              onClick={() => onSelectEdition(selected ? null : edition.id)}
              className={`relative rounded-xl border-2 overflow-hidden text-left transition-colors ${
                selected
                  ? 'border-brand-600'
                  : edition.sold_out
                    ? 'border-neutral-border opacity-60 cursor-not-allowed'
                    : 'border-neutral-border hover:border-neutral-400'
              }`}
              aria-pressed={selected}
              aria-label={`${edition.name}, ${
                edition.sold_out
                  ? 'sold out'
                  : `${edition.available_count} of ${edition.max_supply} remaining`
              }`}
            >
              <div className="relative">
                {imageSrc ? (
                  <img
                    src={imageSrc}
                    alt={edition.name}
                    loading="lazy"
                    className="w-full aspect-square object-cover"
                  />
                ) : (
                  <div className="w-full aspect-square bg-neutral-100" />
                )}
                {edition.sold_out && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <span className="px-3 py-1 rounded-full bg-error-600 text-white text-caption-bold font-caption-bold uppercase tracking-wide">
                      Sold Out
                    </span>
                  </div>
                )}
                {selected && (
                  <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-brand-600 flex items-center justify-center">
                    <FeatherCheck className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>
              <div className="p-2.5">
                <div className="text-body-bold font-body-bold text-default-font truncate">
                  {edition.name}
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-caption font-caption text-default-font">
                    {edition.effective_price_drops === 0
                      ? 'Free'
                      : `${priceXrp.toLocaleString()} XRP`}
                  </span>
                  <span className="text-caption font-caption text-subtext-color">
                    {edition.sold_out
                      ? `0/${edition.max_supply}`
                      : `${edition.available_count} of ${edition.max_supply} left`}
                  </span>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
